/**
 * Error thrown when an upstream resource responds with HTTP 404. Carrying the
 * status lets query retry policies skip retrying a genuine "not found" instead
 * of hammering the endpoint two more times.
 */
export class NotFoundError extends Error {
	readonly status = 404;

	constructor(message = "Not found") {
		super(message);
		this.name = "NotFoundError";
	}
}

/** Narrows an unknown error to an HTTP 404 not-found response. */
export function isNotFoundError(error: unknown): boolean {
	if (error instanceof NotFoundError) {
		return true;
	}
	return (
		typeof error === "object" &&
		error !== null &&
		"status" in error &&
		(error as { status: unknown }).status === 404
	);
}

/**
 * React Query retry predicate that never retries a 404 and otherwise retries up
 * to `maxRetries` times. Avoids the retry storm that turns a single missing
 * resource into three failed requests.
 */
export function retryUnlessNotFound(
	maxRetries = 2,
): (failureCount: number, error: unknown) => boolean {
	return (failureCount, error) =>
		!isNotFoundError(error) && failureCount < maxRetries;
}
